import React, { useEffect, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { useI18n } from "../context/I18nContext";

// Floating WhatsApp CTA, bottom-right. Hidden when no number configured.
export function WhatsAppFloat({ settings }) {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowHint(true), 4500);
    return () => clearTimeout(t);
  }, []);

  // Build a wa.me URL from either explicit phone (digits only) or stored URL
  const phone = (settings?.whatsapp_phone || "").replace(/\D/g, "");
  const fallbackUrl = settings?.whatsapp;
  if (!phone && !fallbackUrl) return null;

  const text = lang === "tr"
    ? (settings?.whatsapp_message_tr || "Merhaba YGS, web sitenizden ulaşıyorum. Bir teklif istiyorum.")
    : (settings?.whatsapp_message_en || "Hi YGS, I'm reaching out from your website. I'd like to request a quote.");
  const href = phone
    ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
    : fallbackUrl;

  const ctaLabel = lang === "tr" ? "Hemen Teklif Al" : "Get a Quote";
  const subLabel = lang === "tr" ? "WhatsApp ile yaz" : "Chat on WhatsApp";

  return (
    <div
      data-testid="whatsapp-float"
      className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3"
    >
      {/* Hint bubble */}
      {showHint && !open && (
        <div
          data-testid="whatsapp-hint"
          className="bg-white border border-[#0d1b2a]/10 rounded-2xl rounded-br-sm shadow-2xl p-4 max-w-xs animate-in fade-in slide-in-from-bottom-2 duration-500 relative"
          style={{ animationFillMode: "forwards" }}
        >
          <button
            onClick={() => setShowHint(false)}
            className="absolute top-2 right-2 text-[#0d1b2a]/40 hover:text-[#0d1b2a]"
            data-testid="whatsapp-hint-close"
            aria-label="Kapat"
          >
            <X size={14} />
          </button>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#0d1b2a]/60">
            YGS · 24/7
          </div>
          <div
            className="text-sm font-bold text-[#0d1b2a] mt-1 pr-4"
            style={{ fontFamily: "Chivo, Inter, sans-serif" }}
          >
            {lang === "tr"
              ? "Soru veya teklif için hemen yazın."
              : "Got a question? Get a quote in minutes."}
          </div>
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1eb057] text-white text-xs font-bold px-3 py-2 rounded-md transition-colors"
            data-testid="whatsapp-hint-cta"
          >
            <MessageCircle size={14} /> {ctaLabel}
          </a>
        </div>
      )}

      {/* Main pill button */}
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        data-testid="whatsapp-cta"
        className="group bg-[#25D366] hover:bg-[#1eb057] text-white shadow-2xl rounded-full pl-4 pr-5 py-3 flex items-center gap-3 transition-all hover:scale-[1.04]"
      >
        <span className="relative flex h-9 w-9 items-center justify-center bg-white/15 rounded-full">
          <MessageCircle size={18} fill="white" strokeWidth={0} />
          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-[#ffb703] rounded-full border-2 border-[#25D366]" />
        </span>
        <div className="text-left">
          <div className="text-xs leading-tight opacity-90">{subLabel}</div>
          <div className="text-sm font-bold leading-tight" style={{ fontFamily: "Chivo, Inter, sans-serif" }}>
            {ctaLabel}
          </div>
        </div>
      </a>
    </div>
  );
}
